/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import APIManager from "../../ApiManager/ApiManager";
import {
  FETCH_USER_DATA_REQUEST,
  FETCH_USER_DATA_SUCCESS,
  FETCH_USER_DATA_FAILURE,
  FETCH_ACTIVE_USERS_SUCCESS,
  FETCH_PENDING_USERS_SUCCESS,
  FETCH_DEACTIVATED_USERS_SUCCESS,
  FETCH_ACTIVE_USERS_COMPANIES,
  FETCH_ACTIVE_USERS_DOMAIN_COMPANIES,
  ACCEPT_USER_REQUEST,
  FETCH_USER_DATA_COUNT,
  FETCH_USER_DATA_COUNT_REQUEST,
} from "./GlobalAdminUsersTypes";
import { COMMON_ERROR } from "../ApplicationDetailsError/ApplicationDetailsErrorTypes";

export const fetchGlobalAdminUserDataRequest = () => {
  return {
    type: FETCH_USER_DATA_REQUEST,
  };
};

export const fetchGlobalAdminUserDataSuccess = (userData) => {
  return {
    type: FETCH_USER_DATA_SUCCESS,
    payload: userData,
  };
};

export const fetchActiveUsersSuccess = (userData) => ({
  type: FETCH_ACTIVE_USERS_SUCCESS,
  payload: userData,
});

export const fetchPendingUsersSuccess = (userData) => ({
  type: FETCH_PENDING_USERS_SUCCESS,
  payload: userData,
});

export const fetchDeactivatedUsersSuccess = (userData) => ({
  type: FETCH_DEACTIVATED_USERS_SUCCESS,
  payload: userData,
});

export const fetchAcceptUserDomainCompanies = (data) => {
  return {
    type: FETCH_ACTIVE_USERS_DOMAIN_COMPANIES,
    payload: data,
  };
};

export const fetchCompanies = (data) => {
  return {
    type: FETCH_ACTIVE_USERS_COMPANIES,
    payload: data,
  };
};

export const acceptUserRequest = (data) => {
  return {
    type: ACCEPT_USER_REQUEST,
    payload: data,
  };
};

export const fetchGlobalAdminUserDataFailure = (error) => {
  return {
    type: FETCH_USER_DATA_FAILURE,
    payload: error,
  };
};

export const fetchGlobalCount = (data) => {
  return {
    type: FETCH_USER_DATA_COUNT,
    payload: data,
  };
};

export const fetchGlobalCountRequest = () => {
  return {
    type: FETCH_USER_DATA_COUNT_REQUEST,
  };
};

export const toggleMFAAuth = (data, refetchPayload) => {
  return async function (dispatch) {
    try {
      if (!data?.id) {
        throw new Error("Invalid MFA payload: missing user id");
      }

      const payload = {
        ...data,
        customMFAEnabled: Boolean(data.customMFAEnabled),
        userMFASettingList: data.userMFASettingList ?? [],
      };

      //PATCH CALL to toggle MFA
      await APIManager.toggleMFA(payload);

      // Refetch the user data after successful MFA toggle
      if (refetchPayload) {
        dispatch(fetchGlobalAdminUserDetails(refetchPayload));
      }

      await dispatch(fetchGlobalAdminUsersCount());
    } catch (error) {
      dispatch(fetchGlobalAdminUserDataFailure(error));
    }
  };
};

export const fetchGlobalAdminUserDetails = (statusData) => {
  return async function (dispatch) {
    dispatch(fetchGlobalAdminUserDataRequest());
    try {
      const data = await APIManager.getGlobalAdminUsers(statusData);
      const statusTab = statusData.status[0];
      switch (statusTab) {
        case "ACTIVE":
          dispatch(fetchActiveUsersSuccess(data));
          break;
        case "PENDING":
          dispatch(fetchPendingUsersSuccess(data));
          break;
        case "DEACTIVATED":
          dispatch(fetchDeactivatedUsersSuccess(data));
          break;
        default:
          dispatch(fetchGlobalAdminUserDataFailure("Unknown status"));
      }
    } catch (error) {
      dispatch(fetchGlobalAdminUserDataFailure(error));
      dispatch(commonError(error, FETCH_USER_DATA_FAILURE));
    }
  };
};

export const fetchGlobalAdminUsersCount = () => {
  return async function (dispatch) {
    // Dispatch loading request to indicate counts are being fetched
    dispatch(fetchGlobalCountRequest());
    try {
      const activeUsers = await APIManager.getGlobalAdminUsers({
        status: ["ACTIVE"],
      });

      const requestedUsers = await APIManager.getGlobalAdminUsers({
        status: ["PENDING"],
      });

      const deactivatedUsers = await APIManager.getGlobalAdminUsers({
        status: ["DEACTIVATED"],
      });

      dispatch(
        fetchGlobalCount({
          activeUsersDataCount: activeUsers.metadata.count,
          requestUsersDataCount: requestedUsers.metadata.count,
          deactivatedUsersDataCount: deactivatedUsers.metadata.count,
        })
      );
    } catch (error) {
      dispatch(fetchGlobalAdminUserDataFailure(error));
    }
  };
};

export const fetchAcceptUserAPIData = (isOemAdmin) => {
  return async function (dispatch) {
    try {
      let oem_admin_roles;
      const domains = await APIManager.getAcceptUserDomains();
      const companies = await APIManager.getAcceptUserCompanies();
      if (isOemAdmin) {
        oem_admin_roles = await APIManager.getOemAdminRoles();
      } else {
        oem_admin_roles = [];
      }
      dispatch(
        fetchAcceptUserDomainCompanies({
          domains,
          companies,
          oem_admin_roles,
        })
      );
    } catch (error) {
      dispatch(fetchGlobalAdminUserDataFailure(error));
    }
  };
};

export const addNewCompany = (postData) => {
  return async function (dispatch) {
    try {
      await APIManager.addNewCompanyMethod(postData);
      const companies = await APIManager.getAcceptUserCompanies();
      dispatch(fetchCompanies(companies));
    } catch (err) {
      dispatch(fetchGlobalAdminUserDataFailure(err));
    }
  };
};

export const acceptNewUserRequest = (acceptUserPayload, userId, searchTerm) => {
  return async function (dispatch) {
    try {
      await APIManager.acceptUserRequest(acceptUserPayload, userId);
      const payload = {
        status: ["PENDING"],
      };
      // Only add email to payload if searchTerm is not empty
      if (searchTerm && searchTerm.trim() !== "") {
        payload.email = [searchTerm];
      }
      const updatedRequestedUsers = await APIManager.getGlobalAdminUsers(
        payload
      );
      dispatch(fetchPendingUsersSuccess(updatedRequestedUsers));
      // Refresh counts after accepting user
      dispatch(fetchGlobalAdminUsersCount());
    } catch (err) {
      dispatch(fetchGlobalAdminUserDataFailure(err));
    }
  };
};

export const rejectUserRequest = (rejectUserPayload, userId, searchTerm) => {
  return async function (dispatch) {
    try {
      await APIManager.rejectUserRequest(rejectUserPayload, userId);
      const payload = {
        status: ["PENDING"],
      };
      // Only add email to payload if searchTerm is not empty
      if (searchTerm && searchTerm.trim() !== "") {
        payload.email = [searchTerm];
      }
      const updatedRequestedUsers = await APIManager.getGlobalAdminUsers(
        payload
      );
      dispatch(fetchPendingUsersSuccess(updatedRequestedUsers));
      // Refresh counts after rejecting user
      dispatch(fetchGlobalAdminUsersCount());
    } catch (err) {
      dispatch(fetchGlobalAdminUserDataFailure(err));
    }
  };
};

export const deactivateUser = (userId, deactivatePayload, searchTerm) => {
  return async function (dispatch) {
    try {
      await APIManager.deactivateUser(userId, deactivatePayload);
      const payload = {
        status: ["ACTIVE"],
      };
      // Only add email to payload if searchTerm is not empty
      if (searchTerm && searchTerm.trim() !== "") {
        payload.email = [searchTerm];
      }
      const updatedActiveUsers = await APIManager.getGlobalAdminUsers(payload);
      dispatch(fetchActiveUsersSuccess(updatedActiveUsers));
      // Refresh counts after deactivating user
      dispatch(fetchGlobalAdminUsersCount());
    } catch (err) {
      dispatch(fetchGlobalAdminUserDataFailure(err));
    }
  };
};

export const reactivateUser = (userId, searchTerm) => {
  return async function (dispatch) {
    try {
      await APIManager.reactivateUser(userId);
      const payload = {
        status: ["DEACTIVATED"],
      };
      // Only add email to payload if searchTerm is not empty
      if (searchTerm && searchTerm.trim() !== "") {
        payload.email = [searchTerm];
      }
      const updatedDeactivatedUsers = await APIManager.getGlobalAdminUsers(
        payload
      );
      dispatch(fetchDeactivatedUsersSuccess(updatedDeactivatedUsers));
      // Refresh counts after reactivating user
      dispatch(fetchGlobalAdminUsersCount());
    } catch (err) {
      dispatch(fetchGlobalAdminUserDataFailure(err));
    }
  };
};

export const deleteUser = (
  userId,
  checkPurgeData,
  forceUninstall,
  refetchPayload
) => {
  return async function (dispatch) {
    try {
      await APIManager.deleteGlobalAdminUser(
        userId,
        checkPurgeData,
        forceUninstall
      );
      await dispatch(fetchGlobalAdminUserDetails(refetchPayload));
      return { success: true };
    } catch (err) {
      dispatch(fetchGlobalAdminUserDataFailure(err));
      dispatch(commonError(err, FETCH_USER_DATA_FAILURE));
      return { success: false, error: err };
    }
  };
};

export const commonError = (error, errorType) => {
  return {
    type: COMMON_ERROR,
    payload: {
      message: error.message,
      errorType: errorType,
    },
  };
};
